import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ArmyBuilderConfig, UserService } from '../../../global';
import { EntityBeforeDeleteHookFn } from '../../config';
import {
    UnitReplacementConfirmationComponent,
    UnitReplacementConfirmationData
} from '../components/unit-replacement-confirmation/unit-replacement-confirmation.component';

/**
 * Creates an onBeforeDelete hook for unit entities that, when affected forces are
 * found, opens a modal offering two deletion modes:
 *
 * - **Replace silently**: swap the unit to a chosen replacement + variant in all
 *   affected forces via POST /userData/forces/replace-unit.  No alert is sent.
 * - **Remove & alert**: delete as normal and push an alert message to each affected
 *   force via POST /userData/alerts so users are notified.
 */
export function createBeforeDeleteWithReplacementHook(): EntityBeforeDeleteHookFn {
    const userService = inject(UserService);
    const config = inject(ArmyBuilderConfig);

    return async (gameId: string, entityType: string, entityId: string, entityName: string) => {
        const url =
            `${config.apiBaseUrl}/userData/forces/affected` +
            `?gameId=${encodeURIComponent(gameId)}` +
            `&entityType=${encodeURIComponent(entityType)}` +
            `&entityId=${encodeURIComponent(entityId)}` +
            `&entityName=${encodeURIComponent(entityName)}`;

        const affectedData: any = await firstValueFrom(userService.adminOnlyRoute('get', url));

        if (!affectedData || affectedData.affectedForcesCount === 0) {
            return null;
        }

        const modalData: UnitReplacementConfirmationData = {
            entityName: affectedData.entityName,
            affectedForcesCount: affectedData.affectedForcesCount,
            affectedUsersCount: affectedData.affectedUsersCount,
            defaultAlertMessage: affectedData.defaultAlertMessage,
            gameId,
            getUnitMeta: (unit: any): string => {
                const parts: string[] = [];
                const factions: string[] = unit.factions ?? [];
                if (factions.length > 0) parts.push(factions.join(', '));
                const badges: string[] = [];
                if (unit.legend) badges.push('Legend');
                if (unit.companion) badges.push('Companion');
                if (badges.length > 0) parts.push(badges.join(', '));
                const weaponSets: any[] = unit.weaponSets ?? [];
                if (weaponSets.length > 0) {
                    const costs = weaponSets.map((ws: any) => ws.cost ?? 0);
                    const min = Math.min(...costs);
                    const max = Math.max(...costs);
                    parts.push(min === max ? `${min} pts` : `${min}\u2013${max} pts`);
                }
                return parts.join(' \u00b7 ');
            }
        };

        return {
            modalComponent: UnitReplacementConfirmationComponent,
            modalData,
            onConfirm: async (dismissData: {
                mode: 'replace' | 'alert';
                replacementEntityId?: string;
                replacementVariantId?: number;
                customAlertMessage?: string;
            }) => {
                if (dismissData.mode === 'replace') {
                    const replaceUrl = `${config.apiBaseUrl}/userData/forces/replace-unit`;
                    await firstValueFrom(
                        userService.adminOnlyRoute('post', replaceUrl, {
                            gameId,
                            fromEntityId: entityId,
                            toEntityId: dismissData.replacementEntityId,
                            toVariantId: dismissData.replacementVariantId
                        })
                    );
                } else {
                    const alertId = `alert-${entityId}-${Date.now()}`;
                    const alerts = affectedData.forces.map((force: any) => ({
                        email: force.email,
                        dataType: 'forces',
                        dataId: force.forceId,
                        alert: {
                            id: alertId,
                            type: 'warning',
                            message: dismissData.customAlertMessage,
                            createdAt: Date.now()
                        }
                    }));
                    const alertsUrl = `${config.apiBaseUrl}/userData/alerts`;
                    await firstValueFrom(userService.adminOnlyRoute('post', alertsUrl, { alerts }));
                }
            },
            onCancel: () => {}
        };
    };
}
